import React, { PropsWithChildren, useState } from 'react';
import { Flex } from 'glints-aries/es';
import { Popover, Typography } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space8, space24 } from 'glints-aries/es/@next/utilities/spacing';
import { get } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { COUNTRY_DOMAINS } from '../common/constants';
import { countryCodeIsIndonesia } from '../common/helpers/countryCodeHelper';
import { getConfig } from '../config';

const Row = styled.div`
  gap: 20px;
  display: flex;
`;

export const TypographyWithBulletPoint = styled(Typography)`
  display: flex;
  column-gap: ${space8};
  color: ${Neutral.B40};
  ::before {
    content: '\u2022';
    padding-inline-start: 10px;
  }
`;

const images = {
  default: {
    npwp: 'images/onboarding/img-tutorial-npwp-en.png',
    offlineStore: 'images/onboarding/img-tutorial-offline-store-en.png',
    onlineAccount: 'images/onboarding/img-tutorial-online-account-en.png',
  },
  [COUNTRY_DOMAINS.id]: {
    npwp: 'images/onboarding/img-tutorial-npwp-id.png',
    offlineStore: 'images/onboarding/img-tutorial-offline-store-id.png',
    onlineAccount: 'images/onboarding/img-tutorial-online-account-id.png',
  },
};

const CustomPopoverStyles = createGlobalStyle`
  .Polaris-Popover__Content {
    height: max-content !important;
    max-width: 600px !important;
  }
`;

export default function CompanyLegalNameAdditionalDetailsPopover({
  children,
}: PropsWithChildren) {
  const { LANG, COUNTRY } = getConfig();
  const image = get(images, LANG, images.default);
  const [isPopoverActive, setIsPopoverActive] = useState(false);
  const [openPopover, closePopover] = [
    () => setIsPopoverActive(true),
    () => setIsPopoverActive(false),
  ];

  const isIndonesia = countryCodeIsIndonesia(COUNTRY);

  if (!isIndonesia) {
    return null;
  }

  return (
    <>
      <CustomPopoverStyles />
      <div onMouseLeave={closePopover}>
        <Popover
          activator={<div onMouseEnter={openPopover}>{children}</div>}
          active={isPopoverActive}
          onClose={closePopover}
          zIndexOverride={1004}
          preferredAlignment="left"
          preferredPosition="above"
          fluidContent={true}
        >
          <Popover.Pane>
            <Popover.Section>
              <Flex
                flexDirection="column"
                gap="20px"
                style={{
                  width: '100%',
                }}
              >
                <Row>
                  <Flex gap="10px" flexDirection="column">
                    <Typography variant="caption" color={Neutral.B18}>
                      <FormattedMessage
                        id="text-business-registered"
                        defaultMessage="For registered businesses (PT/CV)"
                      />
                    </Typography>
                    <TypographyWithBulletPoint variant="subtitle2">
                      <FormattedMessage
                        id="text-business-registered-desc"
                        defaultMessage="Enter your legal company name as shown on your NPWP."
                      />
                    </TypographyWithBulletPoint>
                  </Flex>
                  <img
                    src={image.npwp}
                    style={{
                      maxWidth: 318,
                    }}
                    alt=""
                  />
                </Row>
                <Row>
                  <Flex gap="10px" flexDirection="column">
                    <Typography variant="caption" color={Neutral.B18}>
                      <FormattedMessage
                        id="text-business-individual"
                        defaultMessage="For individual / personal businesses"
                      />
                    </Typography>
                    <TypographyWithBulletPoint variant="subtitle2">
                      <FormattedMessage
                        id="text-business-individual-desc"
                        defaultMessage="Enter your online business account (e.g. Instagram shop, Shopee seller account)."
                      />
                    </TypographyWithBulletPoint>
                    <TypographyWithBulletPoint variant="subtitle2">
                      <FormattedMessage
                        id="text-business-individual-desc-store-name"
                        defaultMessage="Or enter your store name."
                      />
                    </TypographyWithBulletPoint>
                  </Flex>
                  <Row
                    style={{
                      gap: space24,
                    }}
                  >
                    <img
                      src={image.offlineStore}
                      style={{
                        width: 150,
                      }}
                      alt=""
                    />
                    <img
                      src={image.onlineAccount}
                      style={{
                        width: 150,
                      }}
                      alt=""
                    />
                  </Row>
                </Row>
              </Flex>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    </>
  );
}
